import {
  useMemo,
  useCallback,
  MouseEvent,
  useState,
  useEffect,
  useRef,
  MouseEventHandler,
} from 'react';
import NextImage from 'next/image';
import { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';

import { Badge } from '@/core/components/Badge';
import { mapCategories, getSourceIcon } from '@/core/constants/reports';
import { useDecodedCategories } from '@/core/hooks/useDecodedCategories';
import { CARD_DATE_FORMAT } from '@/core/constants/time';
import { Icon } from '@/core/components/Icons';
import { getAppStartTimeSelector, getCategoriesSelector } from '@/core/store/reducers/config';
import { ReportReadStatus } from '@/core/interfaces/common';
import { CARD_READ_TOOLTIP_ID } from '@/core/constants/constants';

import { getTimeAndDate } from '@/utils/helpers';

import {
  CardContainer,
  CardHeader,
  CardFooter,
  CardContent,
  CardHeaderRightContent,
  Country,
  ReportCategory,
  CategoryBadge,
  CategoryMore,
  CardHeaderDates,
  CardTitle,
  CardCategoryBadgeText,
  CardUnpinButton,
  CardHeaderIconsContainer,
  MarkAsButton,
} from './Card.styles';
import { CardProps } from './Card.types';

export const Card = ({
  title,
  country,
  categories,
  source,
  type,
  impact,
  date,
  borderColor,
  onClick,
  onClose,
  headerTitle,
  wasOpened,
  readStatus,
  onMarkAsClick,
}: CardProps) => {
  const checkNewReportTimeout = useRef<number | null>(null);
  const { colors } = useTheme();
  const appStartTime = useSelector(getAppStartTimeSelector);
  const allCategoriesList = useSelector(getCategoriesSelector);
  const [isNewReport, setIsNewReport] = useState(false);

  const decodedCategories = useDecodedCategories(categories);

  const reportDatetime = useMemo(() => (date ? new Date(date).getTime() : undefined), [date]);
  const isReportAfterAppStart = useMemo(() => {
    if (reportDatetime) {
      return reportDatetime > appStartTime;
    }

    return true;
  }, [appStartTime, reportDatetime]);

  const dateFormat = useMemo(
    () => ({
      cardDate: date
        ? getTimeAndDate({
            dateStr: date,
            exitFormat: CARD_DATE_FORMAT,
          })
        : undefined,
      timePassed: date
        ? getTimeAndDate({
            dateStr: date,
            exitFormat: undefined,
            fromNow: true,
          })
        : undefined,
    }),
    [date]
  );

  const handleUnpinButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      onClose?.();
    },
    [onClose]
  );

  const cardBorderColor = useMemo(() => {
    if (borderColor) {
      return borderColor;
    }

    if (impact && (isNewReport || isReportAfterAppStart) && !wasOpened) {
      return colors.impact[impact].backgroundColor;
    }

    return undefined;
  }, [borderColor, colors.impact, impact, isNewReport, isReportAfterAppStart, wasOpened]);

  const handleMarkAsClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    event => {
      event.stopPropagation();
      onMarkAsClick?.();
    },
    [onMarkAsClick]
  );

  useEffect(() => {
    if (reportDatetime) {
      const isNew = reportDatetime > appStartTime;

      if (isNew) {
        setIsNewReport(true);

        checkNewReportTimeout.current = window.setTimeout(() => {
          setIsNewReport(false);
        }, 1000 * 60);
      }
    }

    return () => {
      if (checkNewReportTimeout.current) {
        clearInterval(checkNewReportTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CardContainer
      isNew={isNewReport && !wasOpened}
      borderColor={cardBorderColor}
      onClick={onClick}
    >
      <CardHeader>
        {headerTitle ? (
          headerTitle
        ) : (
          <>
            <Badge
              useFontSizeFactor
              text={type!}
              color={colors.impact[impact!].textColor}
              backgroundColor={colors.impact[impact!].backgroundColor}
            />
            <CardHeaderRightContent>
              <div>
                <CardHeaderDates>{dateFormat.cardDate}</CardHeaderDates>
                <CardHeaderDates>{dateFormat.timePassed}</CardHeaderDates>
              </div>
              <CardHeaderIconsContainer>
                {readStatus && (
                  <MarkAsButton
                    type="button"
                    onClick={handleMarkAsClick}
                    data-tooltip-id={CARD_READ_TOOLTIP_ID}
                    data-tooltip-place="bottom"
                    data-tooltip-content={
                      readStatus === ReportReadStatus.READ ? 'Mark as unread' : 'Mark as read'
                    }
                  >
                    <Icon
                      name={readStatus === ReportReadStatus.READ ? 'EnvelopeOpen' : 'Envelope'}
                      size={16}
                      color={colors.secondaryText}
                    />
                  </MarkAsButton>
                )}
                {source && getSourceIcon(source, colors.secondaryText, 16)}
              </CardHeaderIconsContainer>
            </CardHeaderRightContent>
          </>
        )}
        {onClose && (
          <CardUnpinButton
            type="button"
            onClick={handleUnpinButtonClick}
          >
            <Icon
              name="Thumbtack"
              size={16}
              color={colors.secondaryText}
            />
          </CardUnpinButton>
        )}
      </CardHeader>
      <CardContent>
        {(country || title) && (
          <CardTitle>
            {country && <Country>{country} | </Country>}
            {title}
          </CardTitle>
        )}
        {decodedCategories.length > 0 && (
          <CardFooter>
            {decodedCategories.slice(0, 2).map(category => {
              const categoryData = mapCategories(category, 11, colors.categoryIcon);
              const categoryItem = allCategoriesList.find(item => item.name === category);

              return (
                <ReportCategory key={category}>
                  {categoryItem?.subCategorySvg ? (
                    <NextImage
                      src={categoryItem.subCategorySvg}
                      width={19}
                      height={19}
                      alt={categoryItem.name}
                    />
                  ) : categoryData.icon ? (
                    <CategoryBadge backgroundColor={categoryData.color}>
                      {categoryData.icon}
                    </CategoryBadge>
                  ) : null}
                  <CardCategoryBadgeText>{category}</CardCategoryBadgeText>
                </ReportCategory>
              );
            })}
            {decodedCategories.length > 2 && (
              <CategoryMore>+ {decodedCategories.length - 2} More</CategoryMore>
            )}
          </CardFooter>
        )}
      </CardContent>
    </CardContainer>
  );
};
